import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Pencil, X, Loader2, Settings } from "lucide-react";
import adminApi from "./adminApi";

// Parameters where an empty par_value is allowed on purpose — these drive
// the Facebook/Twitter/Instagram tabs on the public site (see
// PartnerLogos.jsx), and leaving one blank is how that tab gets hidden
// there. Same fixed parid values used on the public side.
const OPTIONAL_VALUE_PARIDS = [31, 32, 33]; // Facebook, Twitter, Instagram

function isOptionalParam(item) {
  if (!item) return false;
  return OPTIONAL_VALUE_PARIDS.indexOf(Number(item.parid)) !== -1;
}

export default function ManageSetting() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingParam, setEditingParam] = useState(null); // { parid, parameter, par_value }
  const [valueInput, setValueInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [toast, setToast] = useState(null);
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 2600);
  };

  const fetchItems = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminApi.get("/admin/settings");
      setItems(res.data.parameters || []);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load parameters.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // ── Edit modal — click a row to open it directly ────────────────────────
  const openEditModal = (item) => {
    setEditingParam(item);
    setValueInput(item.par_value || "");
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    // Facebook/Twitter/Instagram may be saved blank on purpose (hides that
    // tab on the public site). Every other parameter still requires a value.
    const valueRequired = !isOptionalParam(editingParam);

    if (valueRequired && !valueInput.trim()) {
      setFormError("Value is required.");
      return;
    }

    setSaving(true);
    try {
      await adminApi.put(`/admin/settings/${editingParam.parid}`, { par_value: valueInput.trim() });
      showToast("success", "Parameter updated.");
      setShowModal(false);
      fetchItems();
    } catch (err) {
      setFormError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
    {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 shrink-0 mb-2">
        <div>
          <h3 className="font-display font-bold text-[16px] bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] bg-clip-text text-transparent tracking-tight">
            Manage Setting
          </h3>
        </div>
      </div>

      {/* Table card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
            <Loader2 size={18} className="animate-spin" /> Loading parameters...
          </div>
        ) : error ? (
          <div className="py-16 text-center text-sm text-red-500">{error}</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <Settings size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-400">No parameters available.</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm border-collapse">
             <thead className="sticky top-0 z-10 shadow-md">
                <tr className="text-left text-sm">
                  <th className="th-colorful px-5 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Parameter</th>
                  <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2">Value</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence initial={false}>
                  {items.map((p, i) => (
                    <motion.tr
                      key={p.parid}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, delay: i * 0.02 }}
                      onClick={() => openEditModal(p)}
                      className="cursor-pointer hover:bg-[#0EA5A4]/30 transition-colors duration-150 border-b border-slate-200"
                    >
                      <td className="px-5 py-2 text-slate-800 font-medium border-r border-slate-200">{p.parameter}</td>
                      <td className="px-3 py-2 text-slate-500 max-w-[400px] truncate" title={p.par_value}>
                        {p.par_value || "—"}
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success" ? "bg-[#0B2545] text-white" : "bg-red-600 text-white"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit modal */}
      <AnimatePresence>
        {showModal && editingParam && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
            className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <h4 className="font-display font-semibold text-[#0B2545]">Edit Parameter</h4>
                <button onClick={closeModal} className="text-slate-400 hover:text-slate-700">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Parameter</label>
                  <input
                    type="text"
                    value={editingParam.parameter}
                    disabled
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-slate-50 text-slate-500 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Value {isOptionalParam(editingParam) ? (
                      <span className="text-slate-400 font-normal">(optional — leave blank to hide this tab)</span>
                    ) : (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <textarea
                    value={valueInput}
                    onChange={(e) => setValueInput(e.target.value)}
                    rows={3}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition resize-none"
                  />
                </div>

                {formError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    {formError}
                  </p>
                )}

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#143A63] text-white font-medium rounded-lg py-2.5 transition disabled:opacity-60"
                  >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Pencil size={15} />}
                    {saving ? "Saving..." : "Update"}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
